import { usePointsInBounds } from 'core/utils/hooks/use-mapbox-bounds';
import { useFeatureLoadReady } from 'core/utils/segment/feature-load-timing.context';
import type { Feature, FeatureCollection, Point } from 'geojson';
import moment from 'moment';
import { useGetAllRegionalOverviewQualimetreV2 } from 'querys/regional-overview/regional-overview';
import type { RegionalOverviewProperties } from 'querys/regional-overview/regional-overview.query.models';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DetailsCollapsibleSection from '../components/details-collapsible-section/details-collapsible-section.component';
import { RegionalModelView } from '../components/model-view';
import RegionalOverviewEmptyScreen from '../components/regional-overview-empty-screen/regional-overview-empty-screen';
import RegionalRiskLegendRisk from '../components/regional-risk-legend-risk/regional-risk-legend-risk.component';
import { useFiltersStore } from '../components/filters/filters.store';
import { ALL_FILTER_VALUE, RegionalOverviewFilterType } from '../regional-overview.models';
import { useRegionalOverviewMock } from '../regional-risk/hooks/use-regional-overview-mock';
import RegionalOverviewMycotoxinMapContainer from './regional-overview-mycotoxin-map-container/regional-overview-mycotoxin-map-container';
import RegionalMycotoxinStatisticsContainer from './regional-overview-mycotoxin-statistics/regional-mycotoxin-statistics-container';
import { regionalMycotoxinLevels } from './regional-overview-mycotoxin.models';
import { useRegionalOverviewReady } from '../regional-overview-navigation';

const RegionalOverviewMycotoxinContainerRender = (): JSX.Element => {
  const [t] = useTranslation();

  const today = moment().format('YYYY-MM-DD');

  const { isRegionalOverViewMocked } = useRegionalOverviewMock();

  const { data, isLoading } = useGetAllRegionalOverviewQualimetreV2(today, isRegionalOverViewMocked);

  const [dataLoaded, setDataLoaded] = useState<boolean>(false);

  const setStoreFeatures = useFiltersStore(state => state.setFeatures);
  const applyValue = useFiltersStore(state => state.applyValue);
  const filteredFeatures = useFiltersStore(state => state.filteredFeatures) as Feature<Point, RegionalOverviewProperties>[];
  const appliedRisk = useFiltersStore(state => state.appliedValues[RegionalOverviewFilterType.MYCOTOXIN_RISK]);

  useEffect(() => {
    setStoreFeatures(data?.features ?? []);
  }, [data, setStoreFeatures]);

  useEffect(() => {
    if (data || !isLoading) setDataLoaded(true);
  }, [data, isLoading]);

  useEffect(() => {
    if (appliedRisk !== undefined && appliedRisk !== ALL_FILTER_VALUE && filteredFeatures.length === 0) {
      applyValue(RegionalOverviewFilterType.MYCOTOXIN_RISK, ALL_FILTER_VALUE);
    }
  }, [appliedRisk, filteredFeatures, applyValue]);

  const filteredPoints = useMemo<FeatureCollection<Point, RegionalOverviewProperties>>(
    () => ({ type: 'FeatureCollection', features: filteredFeatures }),
    [filteredFeatures]
  );

  const { pointsInBounds, updateBounds } = usePointsInBounds(filteredPoints);

  const renderEmptyScreen = dataLoaded && !data?.features.length;
  useRegionalOverviewReady(!isLoading && Boolean(data), renderEmptyScreen);
  useFeatureLoadReady(renderEmptyScreen && !isLoading, { regionalModel: 'mycotoxin' });

  if (renderEmptyScreen) return <RegionalOverviewEmptyScreen />;

  return (
    <RegionalModelView
      dataTestId='regional-overview-mycotoxin-container'
      map={
        <div className='sd-regional-overview-mycotoxin'>
          <RegionalOverviewMycotoxinMapContainer
            filteredPoints={filteredPoints}
            isLoading={isLoading}
            onBoundsChanged={updateBounds}
            isNavigationDataReady={!isLoading && Boolean(data)}
          />
          <RegionalRiskLegendRisk
            riskLevels={regionalMycotoxinLevels}
            showAtBottom
            title={t('pages.regional_overview.regional_mycotoxin.legend_title')}
          />
        </div>
      }
      list={
        <DetailsCollapsibleSection>
          <RegionalMycotoxinStatisticsContainer dataFiltered={pointsInBounds} />
        </DetailsCollapsibleSection>
      }
    />
  );
};

const RegionalOverviewMycotoxinContainer = React.memo(RegionalOverviewMycotoxinContainerRender);

export default RegionalOverviewMycotoxinContainer;
